import React from 'react';
import Image from 'next/image';
import styles from './style.module.scss';
import { useTranslation } from 'next-i18next';
import { LOCALE_COMMON_PATHS, Namespaces } from 'types/locales';
import type { ClientInfoPageAgent } from 'data/client-info-pages';

export interface AgentContactCardProps {
  /** Agent information */
  agent: ClientInfoPageAgent;
  /** Custom message from the agent */
  customMessage?: string;
}

/**
 * Displays agent contact information and custom message for custom info pages.
 */
const AgentContactCard = ({ agent, customMessage }: AgentContactCardProps) => {
  const { t } = useTranslation(Namespaces.COMMON);

  const hasContactInfo = agent.phone || agent.email || agent.website_url;

  return (
    <div className={styles.component}>
      <div className={styles.card}>
        <div className={styles.agentInfo}>
          <div className={styles.avatarContainer}>
            {agent.photo_url ? (
              <Image
                src={agent.photo_url}
                alt={agent.name}
                width={80}
                height={80}
                className={styles.avatar}
              />
            ) : (
              <div className={styles.avatarPlaceholder}>
                {agent.first_name?.charAt(0) || agent.name.charAt(0)}
              </div>
            )}
          </div>
          <div className={styles.agentDetails}>
            <h3 className={styles.agentName}>{agent.name}</h3>
            <p className={styles.agentTitle}>{t(LOCALE_COMMON_PATHS.CITYINFO_AGENTCONTACTCARD_AGENTTITLE, { defaultValue: 'Your Real Estate Agent' })}</p>
          </div>
        </div>

        {customMessage && (
          <div className={styles.customMessage}>
            <p className={styles.messageText}>{customMessage}</p>
          </div>
        )}

        {hasContactInfo && (
          <div className={styles.contactActions}>
            {agent.phone && (
              <a href={`tel:${agent.phone}`} className={styles.contactButton}>
                <svg className={styles.icon} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                  <path d="M22 16.92v3a2 2 0 0 1-2.18 2 19.79 19.79 0 0 1-8.63-3.07 19.5 19.5 0 0 1-6-6 19.79 19.79 0 0 1-3.07-8.67A2 2 0 0 1 4.11 2h3a2 2 0 0 1 2 1.72 12.84 12.84 0 0 0 .7 2.81 2 2 0 0 1-.45 2.11L8.09 9.91a16 16 0 0 0 6 6l1.27-1.27a2 2 0 0 1 2.11-.45 12.84 12.84 0 0 0 2.81.7A2 2 0 0 1 22 16.92z" />
                </svg>
                {t(LOCALE_COMMON_PATHS.CITYINFO_AGENTCONTACTCARD_CALLBUTTON, { defaultValue: 'Call' })}
              </a>
            )}
            {agent.email && (
              <a href={`mailto:${agent.email}`} className={styles.contactButton}>
                <svg className={styles.icon} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                  <path d="M4 4h16c1.1 0 2 .9 2 2v12c0 1.1-.9 2-2 2H4c-1.1 0-2-.9-2-2V6c0-1.1.9-2 2-2z" />
                  <polyline points="22,6 12,13 2,6" />
                </svg>
                {t(LOCALE_COMMON_PATHS.CITYINFO_AGENTCONTACTCARD_EMAILBUTTON, { defaultValue: 'Email' })}
              </a>
            )}
            {agent.website_url && (
              <a
                href={agent.website_url}
                target="_blank"
                rel="noopener noreferrer"
                className={styles.contactButton}
              >
                <svg className={styles.icon} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                  <circle cx="12" cy="12" r="10" />
                  <line x1="2" y1="12" x2="22" y2="12" />
                  <path d="M12 2a15.3 15.3 0 0 1 4 10 15.3 15.3 0 0 1-4 10 15.3 15.3 0 0 1-4-10 15.3 15.3 0 0 1 4-10z" />
                </svg>
                {t(LOCALE_COMMON_PATHS.CITYINFO_AGENTCONTACTCARD_WEBSITEBUTTON, { defaultValue: 'Website' })}
              </a>
            )}
          </div>
        )}
      </div>
    </div>
  );
};

export default AgentContactCard;
